import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/shared/shadcn/ui/Button';
import { Input } from '@/shared/shadcn/ui/input';
import { DatePicker } from '@/shared/shadcn/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/shared/shadcn/ui/dialog';
import { Send } from '@/shared/icons';
import { toast } from 'react-toastify';
import { useUser } from '@/features/Auth/hooks/useUser';
import { useSuccessDialog } from '@/shared/hooks/useSuccessDialog';
import SuccessDialog from '@/shared/components/SuccessDialog';
import ConfirmationDialog from '@/shared/components/ConfirmationDialog';
import { usePatientInvite } from '@/features/PatientMaster/hooks/usePatientInvite';
import { linkPatient } from '@/features/PatientMaster/api/onboardingService';
import { unlinkPatient } from '@/features/PatientMaster/api/unlinkPatientService';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

interface LinkNewPatientFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (patientData: PatientFormData) => void;
}

const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

const patientFormSchema = z.object({
  lastName: z.string()
    .min(1, 'Last Name is required')
    .max(50, 'Last Name must not exceed 50 characters')
    .regex(/^[a-zA-Z\s'-]+$/, 'Last Name can only contain letters, spaces, hyphens, and apostrophes'),

  firstName: z.string()
    .min(1, 'First Name is required')
    .max(50, 'First Name must not exceed 50 characters')
    .regex(/^[a-zA-Z\s'-]+$/, 'First Name can only contain letters, spaces, hyphens, and apostrophes'),

  dateOfBirth: z.date({
      message: 'Date of Birth is required',
    })
    .refine((date) => {
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return false;
      }
      return true;
    }, 'Please enter a valid date')
    .refine((date) => {
      if (!date) return false;
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      return date <= today;
    }, 'Date of Birth cannot be in the future')
    .refine((date) => {
      if (!date) return false;
      const minDate = new Date(1900, 0, 1);
      return date >= minDate;
    }, 'Please enter a valid date of birth'),

  email: z.string()
    .min(1, 'Email is required')
    .max(100, 'Email must not exceed 100 characters')
    .refine((val) => EMAIL_REGEX.test(val), 'Please enter a valid email address (e.g., example@domain.com)')
    .transform((val) => val.toLowerCase()),

  phoneNumber: z.string()
    .refine((val) => {
      if (!val || val.trim() === '') return true;
      return /^[0-9]{6,15}$/.test(val);
    }, 'Please enter a valid phone number')
});

type PatientFormData = z.infer<typeof patientFormSchema>;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const formatDateOfBirth = (dateOfBirth: string | null | undefined) => {
  if (!dateOfBirth) return '—';
  const match = dateOfBirth.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return dateOfBirth;
  const [, year, month, day] = match;
  const monthIndex = Number(month) - 1;
  if (monthIndex < 0 || monthIndex > 11) return dateOfBirth;
  return `${MONTHS[monthIndex]} ${Number(day)}, ${year}`;
};

const formatPhoneNumber = (phone: string | null | undefined) => {
  if (!phone) return '—';
  const digits = phone.replace(/\D/g, '');
  if (!digits) return '—';
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)})${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
};

const getInitials = (firstName: string | null | undefined, lastName: string | null | undefined) => {
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || 'P';
};

const LinkNewPatientForm: React.FC<LinkNewPatientFormProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { portalAccountId, authUserId } = useUser();
  const { isOpen: isSuccessOpen, showSuccess, hideSuccess, successOptions } = useSuccessDialog();

  const [dateValidationError, setDateValidationError] = useState<string | undefined>(undefined);

  const {
    sendInvite,
    loading: inviteLoading,
    error: inviteError,
    success: inviteSuccess,
    successMessage: inviteSuccessMessage,
    existingPatient,
    reset: resetInvite,
  } = usePatientInvite();

  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [showUnlinkDialog, setShowUnlinkDialog] = useState(false);
  const [unlinkLoading, setUnlinkLoading] = useState(false);

  const [submittedPatientName, setSubmittedPatientName] = useState<string>('');

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
    trigger,
  } = useForm<PatientFormData>({
    resolver: zodResolver(patientFormSchema),
    defaultValues: {
      lastName: '',
      firstName: '',
      dateOfBirth: undefined as unknown as Date,
      email: '',
      phoneNumber: ''
    },
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const isBusy = inviteLoading || linkLoading || unlinkLoading;

  const handleClose = () => {
    if (isBusy) return;
    reset();
    setDateValidationError(undefined);
    setSubmittedPatientName('');
    resetInvite();
    onClose();
  };

  // New patient invite success → close + show success dialog
  useEffect(() => {
    if (inviteSuccess && inviteSuccessMessage) {
      onSuccess?.({
        firstName: submittedPatientName.split(' ')[0] || '',
        lastName: submittedPatientName.split(' ').slice(1).join(' ') || '',
      } as PatientFormData);

      reset();
      setDateValidationError(undefined);

      showSuccess({
        title: 'Invitation Sent!',
        description: inviteSuccessMessage.trim() || 'Invitation has been sent successfully.',
        autoCloseDelay: 3000,
        size: 'md',
      });

      setSubmittedPatientName('');
      resetInvite();
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteSuccess, inviteSuccessMessage]);

  // Invite error → toast
  useEffect(() => {
    if (inviteError) {
      toast.error(inviteError);
    }
  }, [inviteError]);

  const onSubmit = async (data: PatientFormData) => {
    if (!portalAccountId || !authUserId) {
      toast.error('User context not available. Please refresh and try again.');
      return;
    }

    const dateOfBirthStr = data.dateOfBirth
      ? `${data.dateOfBirth.getFullYear()}-${String(data.dateOfBirth.getMonth() + 1).padStart(2, '0')}-${String(data.dateOfBirth.getDate()).padStart(2, '0')}`
      : '';

    setSubmittedPatientName(`${data.firstName} ${data.lastName}`.trim());

    await sendInvite(
      data.firstName,
      data.lastName,
      dateOfBirthStr,
      data.email,
      data.phoneNumber || ''
    );
  };

  const handleDismissExistingPatient = () => {
    if (isBusy) return;
    resetInvite();
  };

  const handleConfirmLink = async () => {
    if (!existingPatient?.userId || !portalAccountId) {
      toast.error('Unable to link this patient right now.');
      return;
    }

    setLinkLoading(true);
    try {
      const response = await linkPatient(existingPatient.userId, portalAccountId);
      toast.success(response.message || 'Patient linked successfully');
      setShowLinkDialog(false);
      reset();
      setDateValidationError(undefined);
      resetInvite();
      onClose();
    } catch (error) {
      console.error('Failed to link existing patient:', error);
      toast.error('Failed to link patient');
    } finally {
      setLinkLoading(false);
    }
  };

  const handleConfirmUnlink = async () => {
    if (!existingPatient?.userId || !portalAccountId) {
      toast.error('Unable to unlink this patient right now.');
      return;
    }

    setUnlinkLoading(true);
    try {
      const response = await unlinkPatient(existingPatient.userId, portalAccountId);
      toast.success(response.responseMsg || 'Patient unlinked successfully');
      setShowUnlinkDialog(false);
      reset();
      setDateValidationError(undefined);
      resetInvite();
      onClose();
    } catch (error) {
      console.error('Failed to unlink existing patient:', error);
      toast.error('Failed to unlink patient');
    } finally {
      setUnlinkLoading(false);
    }
  };

  const isExistingPatientLinked = Boolean(
    existingPatient?.partnerPatientId && String(existingPatient.partnerPatientId).trim() !== ''
  );

  const existingPatientFullName = existingPatient
    ? [existingPatient.firstName, existingPatient.lastName].filter(Boolean).join(' ') || '—'
    : '';

  const existingPatientMessage = isExistingPatientLinked
    ? 'This patient is already registered with Hello Health and linked to your practice.'
    : 'This patient is already registered with Hello Health. Do you want to link this patient to your practice?';

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="bg-white w-full max-w-md p-0 gap-0 border-0 shadow-lg">
          {/* Header */}
          <div className="flex gap-[12px] h-[30.167px] items-center justify-start p-[16px] pb-0">
            <DialogTitle className="font-bold text-xl leading-[30px] text-ink flex-1 m-0">
              Add New Patient
            </DialogTitle>
          </div>

          {/* Divider */}
          <div className="bg-[rgba(220,225,255,0.91)] h-px mx-4 mt-[12px]" />

          {existingPatient ? (
            <div className="p-[16px] pt-[12px] space-y-3">
              {/* Banner */}
              <div className="flex items-start gap-2 bg-[rgba(220,225,255,0.91)] px-3 py-2 text-base font-medium leading-[20px] text-ink">
                <i className="bi bi-exclamation text-[#CA6510] text-base leading-[20px] mt-0.5 flex" aria-hidden="true" />
                <span>{existingPatientMessage}</span>
              </div>

              {/* Compact patient card (vertical stack to fit narrow popup) */}
              <div className="rounded-md border border-[#E1E5EA] bg-white shadow-[0px_2px_4px_#00000013] overflow-hidden">
                <div className="bg-[rgba(220,225,255,0.30)] p-4 space-y-3">
                  {/* Avatar + Name + DOB */}
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F26D6D] text-sm font-medium text-white ">
                      {getInitials(existingPatient.firstName, existingPatient.lastName)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[15px] font-medium leading-[20px] text-ink ">
                        {existingPatientFullName}
                      </p>
                      <p className="text-[13px] leading-[18px] text-secondary-text ">
                        {formatDateOfBirth(existingPatient.dateOfBirth)}
                      </p>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-2 min-w-0">
                    <i className="bi bi-envelope text-secondary-text text-sm leading-[20px] mt-0.5 shrink-0" aria-hidden="true" />
                    <p className="truncate text-sm leading-[20px] text-ink ">
                      {existingPatient.email || '—'}
                    </p>
                  </div>

                  {/* Phone */}
                  <div className="flex items-start gap-2 min-w-0">
                    <i className="bi bi-telephone text-secondary-text text-sm leading-[20px] mt-0.5 shrink-0" aria-hidden="true" />
                    <p className="text-sm leading-[20px] text-ink ">
                      {existingPatient.mobileNumber || '—'}
                    </p>
                  </div>

                  {/* Action icons */}
                  <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#E1E5EA]">
                    {isExistingPatientLinked ? (
                      <button
                        type="button"
                        onClick={() => setShowUnlinkDialog(true)}
                        disabled={isBusy}
                        title="Unlink Patient"
                        aria-label="Unlink Patient"
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-danger-subtle hover:bg-danger-subtle/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {unlinkLoading ? (
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-mauve border-t-transparent" />
                        ) : (
                          <img src="/images/img_vector_gray_700_01.svg" alt="unlink" className="h-4 w-4" />
                        )}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowLinkDialog(true)}
                        disabled={isBusy}
                        title="Link Patient"
                        aria-label="Link Patient"
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-info-subtle hover:bg-[#b6d4fe] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {linkLoading ? (
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#0a58ca] border-t-transparent" />
                        ) : (
                          <i className="bi bi-link-45deg text-base text-gray-900 flex"></i>
                        )}
                      </button>
                    )}

                    {/* Chat — placeholder, wired later */}
                    {/* <button
                      type="button"
                      disabled
                      title="Send Message (coming soon)"
                      aria-label="Send Message (coming soon)"
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-success-subtle opacity-60 cursor-not-allowed"
                    >
                      <MessageSquare className="h-4 w-4 text-success" strokeWidth={2} />
                    </button> */}

                    {/* message button — placeholder, wired later */}
                    {/* <button
                      type="button"
                      disabled
                      title="Send Invite (coming soon)"
                      aria-label="Send Invite (coming soon)"
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-success-subtle opacity-60 cursor-not-allowed"
                    >
                      <Send className="h-4 w-4 text-success" strokeWidth={2} />
                    </button> */}
                  </div>
                </div>

                {/* Cancel footer (returns to form, no API) */}
                <div className="flex justify-end border-t border-[#E1E5EA] bg-white px-4 py-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleDismissExistingPatient}
                    disabled={isBusy}
                    className="h-[36px] min-w-[92px] border border-field-border bg-white px-[13px] py-[7px] text-sm font-normal leading-[20px] text-secondary-text hover:bg-surface-subtle"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="p-4 pt-1.5">
              <div className="bg-white flex flex-col gap-2 items-start justify-center pb-0 pt-2 px-2 w-full">
                {/* Last Name */}
                <div className="bg-transparent w-full">
                  <div className="flex flex-col items-start justify-start pb-[8px] pt-0 px-0 w-full">
                    <div className="flex gap-1 items-end justify-start pb-[8px] pt-0 px-0 w-full">
                      <div className="flex flex-col items-start justify-center overflow-clip">
                        <div className="font-normal text-base leading-[1.5] text-body ">
                          Last Name
                        </div>
                      </div>
                      <div className="flex flex-col items-start justify-center overflow-clip">
                        <div className="font-normal text-base leading-[1.5] text-danger ">
                          *
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-start rounded-sm w-full">
                      <Input
                        {...register('lastName')}
                        className={`bg-white border border-field-border rounded-sm px-[13px] py-[7px] text-base font-normal leading-[1.5] text-secondary-text w-full h-auto focus-visible:ring-0 focus-visible:ring-offset-0 ${
                          errors.lastName ? 'border-danger' : ''
                        }`}
                        placeholder=""
                      />
                    </div>
                    {errors.lastName && (
                      <span className="text-danger text-xs font-normal mt-1 ">
                        {errors.lastName.message}
                      </span>
                    )}
                  </div>
                </div>

                {/* First Name */}
                <div className="bg-transparent w-full">
                  <div className="flex flex-col items-start justify-start pb-[8px] pt-0 px-0 w-full">
                    <div className="flex gap-1 items-end justify-start pb-[8px] pt-0 px-0 w-full">
                      <div className="flex flex-col items-start justify-center overflow-clip">
                        <div className="font-normal text-base leading-[1.5] text-body ">
                          First Name
                        </div>
                      </div>
                      <div className="flex flex-col items-start justify-center overflow-clip">
                        <div className="font-normal text-base leading-[1.5] text-danger ">
                          *
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-start rounded-sm w-full">
                      <Input
                        {...register('firstName')}
                        className={`bg-white border border-field-border rounded-sm px-[13px] py-[7px] text-base font-normal leading-[1.5] text-secondary-text w-full h-auto focus-visible:ring-0 focus-visible:ring-offset-0 ${
                          errors.firstName ? 'border-danger' : ''
                        }`}
                        placeholder=""
                      />
                    </div>
                    {errors.firstName && (
                      <span className="text-danger text-xs font-normal mt-1 ">
                        {errors.firstName.message}
                      </span>
                    )}
                  </div>
                </div>

                {/* Date of Birth */}
                <div className="bg-transparent w-full">
                  <div className="flex flex-col items-start justify-start pb-[8px] pt-0 px-0 w-full">
                    <div className="flex gap-1 items-end justify-start pb-[8px] pt-0 px-0 w-full">
                      <div className="flex flex-col items-start justify-center overflow-clip">
                        <div className="font-normal text-base leading-[1.5] text-body ">
                          Date Of Birth
                        </div>
                      </div>
                      <div className="flex flex-col items-start justify-center overflow-clip">
                        <div className="font-normal text-base leading-[1.5] text-danger ">
                          *
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-start rounded-sm w-full">
                      <Controller
                        name="dateOfBirth"
                        control={control}
                        render={({ field }) => (
                          <div className={`bg-white border rounded-sm px-[13px] py-[7px] w-full h-auto focus-visible:ring-0 focus-visible:ring-offset-0 ${
                            errors.dateOfBirth || dateValidationError ? 'border-danger' : 'border-field-border'
                          }`}>
                            <DatePicker
                              date={field.value}
                              onDateChange={(date) => {
                                field.onChange(date);
                                trigger('dateOfBirth');
                              }}
                              onValidationChange={(isValid, message) => {
                                setDateValidationError(isValid ? undefined : message);
                              }}
                              placeholder="MM/DD/YYYY"
                              autoClose={true}
                              allowFutureDates={false}
                              className="w-full h-auto border-none p-0 bg-transparent text-base font-normal leading-[1.5] text-secondary-text "
                            />
                          </div>
                        )}
                      />
                    </div>
                    {(dateValidationError || errors.dateOfBirth) && (
                      <span className="text-danger text-xs font-normal mt-1 mb-2 block ">
                        {dateValidationError || errors.dateOfBirth?.message}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-white flex flex-col gap-2 items-start justify-center overflow-visible px-2 py-0 w-full mt-2">
                <div className="flex flex-col gap-2.5 items-start justify-center w-full">
                  {/* Email */}
                  <div className="bg-transparent flex flex-col items-start justify-start pb-[16px] pt-0 px-0 w-full">
                    <div className="flex gap-1 items-end justify-start pb-[8px] pt-0 px-0 w-full">
                      <div className="flex flex-col items-start justify-center overflow-clip">
                        <div className="font-normal text-base leading-[1.5] text-body ">
                          Email
                        </div>
                      </div>
                      <div className="flex flex-col items-start justify-center overflow-clip">
                        <div className="font-normal text-base leading-[1.5] text-danger ">
                          *
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-start rounded-sm w-full">
                      <Input
                        type="email"
                        {...register('email')}
                        className={`bg-white border border-field-border rounded-sm px-[13px] py-[7px] text-base font-normal leading-[1.5] text-secondary-text w-full h-auto focus-visible:ring-0 focus-visible:ring-offset-0 ${
                          errors.email ? 'border-danger' : ''
                        }`}
                        placeholder="example@domain.com"
                        autoComplete="email"
                      />
                    </div>
                    {errors.email && (
                      <span className="text-danger text-xs font-normal mt-1 ">
                        {errors.email.message}
                      </span>
                    )}
                  </div>
                </div>

                {/* Phone Number */}
                <div className="flex flex-col gap-2.5 items-start justify-center w-full">
                  <div className="font-normal text-[15px] leading-[0.5] text-black ">
                    Phone Number
                  </div>
                  <div className="flex items-center justify-start w-full">
                    <Controller
                      name="phoneNumber"
                      control={control}
                      render={({ field }) => (
                        <PhoneInput
                          country="us"
                          enableSearch={true}
                          disableSearchIcon={true}
                          preferredCountries={['us', 'ca', 'gb']}
                          countryCodeEditable={false}
                          value={field.value || ''}
                          onChange={(value) => field.onChange(value)}
                          containerClass="w-full !relative"
                          inputClass={`!w-full !h-auto !py-[7px] !pl-[52px] !pr-[13px] !text-base !leading-[1.5] !font-normal !text-secondary-text !border !rounded-sm !shadow-none focus:!ring-0 focus:!outline-none ${
                            errors.phoneNumber ? '!border-danger' : '!border-field-border'
                          }`}
                          buttonClass="!bg-surface-muted !border-field-border !rounded-l-[4px]"
                          dropdownClass="!z-[10000]"
                          dropdownStyle={{ top: '40px', bottom: 'auto' }}
                          specialLabel=""
                          inputProps={{
                            name: field.name,
                            onBlur: field.onBlur,
                          }}
                        />
                      )}
                    />
                  </div>
                  {errors.phoneNumber && (
                    <span className="text-danger text-xs font-normal mt-1 ">
                      {errors.phoneNumber.message}
                    </span>
                  )}
                </div>
              </div>

              {/* Action Buttons - RIGHT ALIGNED */}
              <div className="flex gap-2.5 items-center justify-end pt-[17px] px-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isBusy}
                  className="bg-transparent border border-secondary-text text-secondary-text hover:bg-gray-50 px-[13px] py-[7px] rounded-sm font-normal text-base leading-[1.5] h-auto"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isBusy}
                  className="bg-success hover:bg-success-strong text-white border border-success px-[13px] py-[7px] rounded-sm font-normal text-base leading-[1.5] h-auto flex items-center gap-2"
                >
                  {inviteLoading ? 'Sending...' : 'Send Invite'}
                  {!inviteLoading && <Send className="w-4 h-4" />}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Success Dialog */}
      <SuccessDialog
        isOpen={isSuccessOpen}
        onClose={hideSuccess}
        {...successOptions}
      />

      {/* Link Confirmation */}
      <ConfirmationDialog
        isOpen={showLinkDialog}
        onClose={() => {
          if (!linkLoading) setShowLinkDialog(false);
        }}
        onConfirm={handleConfirmLink}
        title="Link Patient?"
        description={`Are you sure you want to link ${existingPatientFullName || 'this patient'}?`}
        confirmText="Link"
        cancelText="Cancel"
        isLoading={linkLoading}
        size="md"
      />

      {/* Unlink Confirmation */}
      <ConfirmationDialog
        isOpen={showUnlinkDialog}
        onClose={() => {
          if (!unlinkLoading) setShowUnlinkDialog(false);
        }}
        onConfirm={handleConfirmUnlink}
        title="Unlink Patient?"
        description={`Are you sure you want to unlink ${existingPatientFullName || 'this patient'}?`}
        confirmText="Unlink"
        cancelText="Cancel"
        iconType="unlink"
        isLoading={unlinkLoading}
        size="md"
      />
    </>
  );
};

export default LinkNewPatientForm;
